/*
  PDP offers – endless auto-advancing snap slider and coupon copy (snippets/pdp-offers.liquid)

  With several offers the slides are cloned once before and once after the real set
  ([clones][real][clones]). The track always rests inside the real set; after any scroll, a
  position in a clone set is swapped instantly for the identical position in the real set. So the
  current offer always sits at the left edge with the next one peeking in on the right, also when
  wrapping from the last offer back to the first.
*/
if (!customElements.get('pdp-offers')) {
  class PdpOffers extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pdp-offer-track]');
      this.status = this.querySelector('[data-pdp-offer-status]');
      this.paused = false;
      this.lastInteraction = 0;

      if (!this.onClick) {
        this.onClick = this.onClick_.bind(this);
        this.onScroll = this.onScroll_.bind(this);
      }
      this.addEventListener('click', this.onClick);

      const slides = this.track ? Array.from(this.track.querySelectorAll('[data-pdp-offer-slide]')) : [];
      this.count = slides.length;
      if (this.count < 2) return;

      if (!this.dataset.looped) {
        this.dataset.looped = 'true';
        const makeClones = () => slides.map((slide) => {
          const clone = slide.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('button').forEach((b) => b.setAttribute('tabindex', '-1'));
          return clone;
        });
        this.track.prepend(...makeClones());
        this.track.append(...makeClones());

        this.addEventListener('mouseenter', () => { this.paused = true; });
        this.addEventListener('mouseleave', () => this.resume());
        this.addEventListener('focusin', () => { this.paused = true; });
        this.addEventListener('focusout', () => this.resume());
        this.track.addEventListener('touchstart', () => { this.paused = true; }, { passive: true });
        this.track.addEventListener('touchend', () => this.resume(), { passive: true });
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        // Keep the current offer aligned when the layout width changes
        this.resizeObserver = new ResizeObserver(() => this.jumpTo(this.index ?? this.count));
      }

      this.resizeObserver.observe(this.track);
      this.jumpTo(this.count);

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.interval = parseInt(this.dataset.interval, 10) || 3000;
      clearInterval(this.timer);
      this.timer = setInterval(() => this.tick(), this.interval);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.removeEventListener('click', this.onClick);
      this.resizeObserver?.disconnect();
    }

    resume() {
      this.paused = false;
      this.lastInteraction = Date.now();
    }

    get step() {
      const items = this.track.children;
      return items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left;
    }

    jumpTo(index) {
      this.index = index;
      this.track.scrollTo({ left: index * this.step, behavior: 'instant' });
    }

    tick() {
      // Wait a full interval after the customer swipes or hovers before moving again
      if (this.paused || document.hidden || Date.now() - this.lastInteraction < this.interval) return;
      this.normalize();
      this.index += 1;
      this.track.scrollTo({ left: this.index * this.step, behavior: 'smooth' });
    }

    // Move from a clone set back to the identical position in the real set
    normalize() {
      const step = this.step;
      let index = Math.round(this.track.scrollLeft / step);
      if (index < this.count) index += this.count;
      else if (index >= this.count * 2) index -= this.count;
      if (Math.abs(this.track.scrollLeft - index * step) > 1) this.jumpTo(index);
      else this.index = index;
    }

    onScroll_() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.normalize(), 150);
    }

    async onClick_(event) {
      const button = event.target.closest('[data-pdp-offer-code]');
      if (!button) return;

      const code = button.dataset.pdpOfferCode;
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        const field = document.createElement('textarea');
        field.value = code;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;opacity:0;';
        document.body.appendChild(field);
        field.select();
        try { document.execCommand('copy'); } catch (e) {}
        field.remove();
      }

      const label = button.querySelector('[data-pdp-offer-label]');
      const copied = this.dataset.copiedLabel || 'Copied!';
      if (label) {
        clearTimeout(button._pdpOfferReset);
        label.dataset.original = label.dataset.original || label.textContent;
        label.textContent = copied;
        button._pdpOfferReset = setTimeout(() => { label.textContent = label.dataset.original; }, 1600);
      }
      if (this.status) this.status.textContent = `${code} ${copied}`;
    }
  }

  customElements.define('pdp-offers', PdpOffers);
}
